import { useMemo } from "react";

import { api, type ApiError } from "./client";
import { useResource } from "./useResource";

// terminals.status enum (data-model.md §9). "exited" is a presentation of a
// daemon-initiated `closed` carrying an exit code, derived in the UI.
export type TerminalStatus = "closed" | "error" | "open" | "provisioning";

// GET /api/v1/tasks/:id/terminals row.
export type ApiTerminal = {
  readonly channel_id: number;
  readonly closed_at: string | null;
  readonly cols: number;
  readonly exit_code: number | null;
  readonly name: string;
  readonly opened_at: string | null;
  readonly public_id: string;
  readonly rows: number;
  readonly shell: string | null;
  readonly status: string;
};

export type TerminalView = {
  readonly channelId: number;
  readonly closedAt: string | null;
  readonly cols: number;
  readonly exitCode: number | null;
  readonly id: string;
  readonly name: string;
  readonly openedAt: string | null;
  readonly rows: number;
  readonly shell: string | null;
  readonly status: TerminalStatus;
};

const TERMINAL_STATUSES: ReadonlyArray<TerminalStatus> = [
  "closed",
  "error",
  "open",
  "provisioning",
];

const mapStatus = (status: string): TerminalStatus =>
  TERMINAL_STATUSES.find((s) => s === status) ?? "error";

export const mapTerminal = (terminal: ApiTerminal): TerminalView => ({
  channelId: terminal.channel_id,
  closedAt: terminal.closed_at,
  cols: terminal.cols,
  exitCode: terminal.exit_code,
  id: terminal.public_id,
  name: terminal.name,
  openedAt: terminal.opened_at,
  rows: terminal.rows,
  shell: terminal.shell,
  status: mapStatus(terminal.status),
});

export type UseTaskTerminals = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly terminals: ReadonlyArray<TerminalView>;
};

// GET /api/v1/tasks/:id/terminals — the task's PTYs (task-scoped, shared across
// viewers). Pass `null` to skip while the task id is missing.
export const useTaskTerminals = (taskId: string | null): UseTaskTerminals => {
  const { data, error, loading, refetch } = useResource<
    ReadonlyArray<ApiTerminal>
  >(taskId ? `/tasks/${taskId}/terminals` : null);
  const terminals = useMemo(() => (data ?? []).map(mapTerminal), [data]);
  return { error, loading, refetch, terminals };
};

export type CreateTerminalInput = {
  readonly cols: number;
  readonly rows: number;
  readonly shell?: string;
};

export type CreatedTerminal = {
  readonly terminal: {
    readonly channel_id: number;
    readonly name: string;
    readonly public_id: string;
    readonly status: string;
  };
};

// POST /api/v1/tasks/:id/terminals — opens a PTY in the task's sandbox. The
// server names the terminal and allocates its channel id. 503
// `sandbox_unavailable` if the task has no live sandbox.
export const createTerminal = (
  taskId: string,
  input: CreateTerminalInput,
): Promise<CreatedTerminal> =>
  api.post<CreatedTerminal>(`/tasks/${taskId}/terminals`, {
    cols: input.cols,
    rows: input.rows,
    shell: input.shell,
  });

// DELETE /api/v1/terminals/:id — kills the PTY (204).
export const closeTerminal = (terminalPublicId: string): Promise<void> =>
  api.del<void>(`/terminals/${terminalPublicId}`);
